// /app/frontend/src/pages/seo/ReportsHubPage.jsx
// Top-level Research & Reports hub — distinct from /guides, focused on
// authority/citation pages for journalists, podcasters, and bloggers.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  ReportHero,
} from './SEOUtils';

const REPORTS = [
  {
    to: '/state-of-black-relationships-2026',
    label: 'The State of Black Relationships Report 2026',
    body: 'An in-depth look at modern Black relationships, dating trends, family-building, intentional partnerships, and connection across the diaspora.',
    type: 'Annual Report',
    date: 'February 2026',
  },
  {
    to: '/state-of-black-poly-families-2026',
    label: 'The State of Black Poly Families Report 2026',
    body: 'How Black poly families are forming, what they share in common, and the future of plural family-building in the diaspora.',
    type: 'Annual Report',
    date: 'February 2026',
  },
  {
    to: '/relationship-trends-african-diaspora',
    label: 'Relationship Trends Across The African Diaspora',
    body: 'Dating, marriage, family-building, community, and relationship trends across the global African diaspora.',
    type: 'Research Analysis',
    date: 'February 2026',
  },
  {
    to: '/family-building-handbook',
    label: 'The Family Building Handbook',
    body: 'A structure-agnostic guide for couples and families serious about building something that lasts.',
    type: 'Handbook',
    date: 'February 2026',
  },
  {
    to: '/how-people-actually-find-a-sister-wife',
    label: 'How People Actually Find A Sister Wife',
    body: 'The honest story behind successful plural family-building — what really happens before, during, and after the search.',
    type: 'Research Analysis',
    date: 'February 2026',
  },
];

export default function ReportsHubPage() {
  const path = '/reports';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Reports', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="UnityLove Research & Reports"
        description="Annual reports, research analyses, and handbooks on modern Black relationships, plural family-building, and dating trends across the African diaspora."
        canonical={path}
        schemas={schemas}
      />

      <ReportHero
        eyebrow="UnityLove Research"
        headline="Research & Reports"
        dek="Annual reports, in-depth analyses, and handbooks on modern Black relationships, plural family-building, and dating across the African diaspora."
      />

      <main className="max-w-3xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-10">
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
            UnityLove Research publishes honest, observation-based analyses of how Black love is being chosen, built, and protected across the diaspora. Our reports are intended to be useful to journalists, researchers, podcasters, and anyone trying to understand the shifts happening in modern Black relationships. Each piece is written in the open about its sources and methods.
          </p>
        </section>

        {/* Reports list */}
        <section className="mb-14">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 dark:text-slate-50 mb-5">
            Published Reports
          </h2>
          <div className="space-y-4">
            {REPORTS.map((r) => (
              <a
                key={r.to}
                href={r.to}
                className="block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 hover:border-rose-400 dark:hover:border-rose-500 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 px-2 py-1 rounded-full">
                    {r.type}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{r.date}</span>
                </div>
                <h3 className="font-playfair text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                  {r.label}
                </h3>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm sm:text-base">
                  {r.body}
                </p>
                <p className="mt-3 text-rose-600 dark:text-rose-400 text-sm font-medium">Read the report →</p>
              </a>
            ))}
          </div>
        </section>

        {/* Coming soon */}
        <section className="mb-14">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">
            Future Annual Reports
          </h2>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
            UnityLove Research publishes annual reports on the state of Black relationships and Black poly families each spring. Future reports will continue tracking the shifts we observe — and the new questions that emerge as the community grows.
          </p>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-5">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <strong className="text-slate-900 dark:text-slate-50">Upcoming:</strong> State of Black Relationships 2027, State of Black Poly Families 2027, and ongoing research analyses throughout the year.
            </p>
          </div>
        </section>

        {/* Press / citation note */}
        <section className="mb-12 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-3">For Press & Researchers</h2>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm sm:text-base mb-3">
            All UnityLove Research reports may be cited with attribution. Each report includes an &ldquo;About This Report&rdquo; section describing the sources of its observations and the language conventions used. For press inquiries, interview requests, or methodology questions, contact the UnityLove team through the channels listed on our contact page.
          </p>
          <a href="/contact" className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-medium text-sm">
            Contact UnityLove →
          </a>
        </section>
      </main>
    </div>
  );
}
